import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'

import styles from './video.module.sass'

import BlockWrapper from '../layout/block_wrapper'

import classNames from 'classnames'
import Image from './image'

import get from 'lodash/get'

function Video(props, ref) {
	let fields = get(props, 'fields.video', props) || null
	let settings = props.settings || null
	let classes = props.className
	let settingsProps = null
	const videoRef = useRef(null)
	const [playing, setPlaying] = useState(false)
	const [lowPowerMode, setLowPowerMode] = useState(true)
	const [areCaptionsEnabled, setAreCaptionsEnabled] = useState(true)
	const vimeoUrl = fields.vimeo_url

	// Support for new video_type logic
	const videoType = fields.video_type || 'self_hosted'
	const isVimeoVideo = Boolean((videoType === 'vimeo' && vimeoUrl) || vimeoUrl)
	const localVimeoIframeRef = useRef(null);
	const vimeoPlayerRef = useRef(null);
	const [isVimeoPlayerReady, setIsVimeoPlayerReady] = useState(false)

	useImperativeHandle(ref, () => ({
		pause: () => {
			if (isVimeoVideo && vimeoPlayerRef.current) {
				vimeoPlayerRef.current.pause();
			} else if (videoRef.current) {
				videoRef.current.pause();
			}
		},
		play: () => {
			if (isVimeoVideo && vimeoPlayerRef.current) {
				vimeoPlayerRef.current.play();
			} else if (videoRef.current) {
				videoRef.current.play();
			}
		}
	}), [isVimeoVideo]);

	useEffect(() => {
		let isMounted = true
		if (!isVimeoVideo || !localVimeoIframeRef.current) {
			return undefined
		}

		import(/* webpackChunkName: "vimeo-player" */ '@vimeo/player').then((playerModule) => {
			if (!isMounted || !localVimeoIframeRef.current) {
				return
			}

			const Player = playerModule.default
			const player = new Player(localVimeoIframeRef.current)
			vimeoPlayerRef.current = player
			player.ready().then(() => {
				if (!isMounted) {
					return
				}

				setIsVimeoPlayerReady(true)
			}).catch(() => {})
		})

		return () => {
			isMounted = false
			setIsVimeoPlayerReady(false)
			if (vimeoPlayerRef.current) {
				vimeoPlayerRef.current.unload();
				vimeoPlayerRef.current = null;
			}
		};
	}, [isVimeoVideo]);

	useEffect(() => {
		if (!isVimeoVideo || !isVimeoPlayerReady || typeof props.modalOpen !== 'boolean' || !vimeoPlayerRef.current) {
			return
		}

		if (props.modalOpen) {
			vimeoPlayerRef.current.play().catch(() => {})
		} else {
			vimeoPlayerRef.current.pause().catch(() => {})
		}
	}, [isVimeoPlayerReady, isVimeoVideo, props.modalOpen])

	if (isVimeoVideo) {
		// Extract Vimeo ID from URL
		const vimeoIdMatch = vimeoUrl && vimeoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
		const vimeoId = vimeoIdMatch ? vimeoIdMatch[1] : null;
		if (!vimeoId) {
			console.error('Invalid Vimeo URL:', vimeoUrl);
			return null;
		}

		return (
			<div className={classNames(styles.video, classes)}>
				<div className={styles.video_iframe_wrapper}>
					<iframe
						ref={localVimeoIframeRef}
						src={`https://player.vimeo.com/video/${vimeoId}?autoplay=0&title=0&byline=0&portrait=0&dnt=1&transparent=1`}
						width="100%"
						height="100%"
						frameBorder="0"
						allow="autoplay; fullscreen; picture-in-picture"
						allowFullScreen
						title={fields.title || 'Vimeo video'}
					/>
				</div>
			</div>
		);
	}

	const toggleCaptions = () => {
		setAreCaptionsEnabled(!areCaptionsEnabled);
	}

	const playVideo = () => {
		videoRef.current.play()
		setPlaying(true)
		setLowPowerMode(false)
	}

	const videoProps = {
		alt: fields.alt,
		title: fields.title,
	}

	const trackProps = {
		kind: "captions",
		src: fields.captions, 
		label: "English", 
	}

	settingsProps = {
		loading: settings?.loading || 'lazy',
		autoPlay: settings?.autoplay || false,
		muted: settings?.muted || false,
		loop: settings?.loop || false,
		playsInline: settings?.playsinline || false,
		...(props.isVideoCard && { areCaptionsEnabled, toggleCaptions }),
	}
	
	if(settings?.autoplay !== false){
		settingsProps.playsInline = true
	}

	if(settings?.controls !== false){
		settingsProps.controls = true
	}

	if(settings?.frosted){
		classes += ' ' + styles.frosted
	}

	if(settings?.texture){
		classes += ' ' + styles.texture
	}

	const sourceProps = {
		src: fields.url,
		type: fields.mime_type,
	}

	useEffect(() => {
		// If video is set to autoplay and it isn't playing, set lowPowerMode to true but check after window loads
		if (settingsProps?.autoPlay && videoRef.current) {
			if (videoRef.current.paused) {
				videoRef.current.play()
				.then(() => {
					setLowPowerMode(false)
				})
				.catch((error) => {
					setLowPowerMode(true)
				})
			} else {
				setLowPowerMode(false)
			}
		}
	}, [settingsProps?.autoPlay, fields.url])

	if(props.layout_settings){
		return <BlockWrapper {...props} className={classNames(styles.video, classes)}>
			<video ref={videoRef} {...videoProps} {...settingsProps}>
				<source {...sourceProps} />
				{fields.captions && areCaptionsEnabled && <track {...trackProps} default srclang="en" />}
			</video>

			{props.fields.cover_video && 
				<div className={classNames(styles.cover_video, playing ? styles.hidden : '')} onClick={() => {playVideo()}}>
					<Video settings={{controls: false, autoplay: true, muted: true, loop: true, loading: 'lazy'}} {...props.fields.cover_video} />
					<div className={styles.overlay_image}></div>
				</div>
			}

			{props.fields.cover_image && 
				<div className={classNames(styles.cover_image, playing && lowPowerMode == false ? styles.hidden : '')} onClick={() => {playVideo()}}>
					<Image {...props.fields.cover_image} settings={{loading: settingsProps.loading}} />
					<div className={styles.overlay_image}></div>
				</div>
			}

			{props.fields.cover_video && 
				<div className={classNames(styles.cover_video, playing ? styles.hidden : '')} onClick={() => {playVideo()}}>
					<Video settings={{controls: false, autoplay: true, muted: true, loop: true, loading: 'lazy'}} {...props.fields.cover_video} />
					<div className={styles.overlay_image}></div>
				</div>
			}

			{props.modalOpen && fields.captions && (
				<button className={styles.videoCardButton} onClick={toggleCaptions}>
					{areCaptionsEnabled ? 'Disable Captions' : 'Enable Captions'}
				</button>
			)}
			{settings?.frosted &&
			<div className={styles.frosted}></div>}
			{settings?.texture &&
			<div className={styles.texture}></div>}
		</BlockWrapper>
	} else {
		return <div className={classNames(styles.video, classes)}>
			<video ref={videoRef} {...videoProps} {...settingsProps}>
				<source {...sourceProps} />
				{fields.captions && areCaptionsEnabled && <track {...trackProps} default srclang="en" />}
			</video>
			{props.fields?.cover_image && 
				<div className={classNames(styles.cover_image, lowPowerMode == false ? styles.hidden : '')}>
					<Image {...props.fields?.cover_image} settings={{loading: settingsProps.loading}} />
				</div>
			}
			{props.modalOpen && fields.captions && (
				<button className={styles.videoCardButton} onClick={toggleCaptions}>
					{areCaptionsEnabled ? 'Disable Captions' : 'Enable Captions'}
				</button>
			)}
			{settings?.frosted &&
			<div className={styles.frosted}></div>}
			{settings?.texture &&
			<div className={styles.texture}></div>}
		</div>
	}
}

export default forwardRef(Video)
